import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { Badge, Icon, InlineNotification, Link as NucleusLink, ModalWindow, Select, Spinner, Table, Text, useLocalizeMessage, } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
const ACTION_OPTIONS = [
    { label: 'Update', value: 'UPDATE' },
    { label: 'Keep current', value: 'KEEP' },
];
export const ReviewVersionsModal = ({ isOpen, onClose, chartRefs, chartsWithNewerVersions, dashboardStatus, onApply, }) => {
    const translate = useLocalizeMessage();
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            hasFetchedRef.current = false;
            return;
        }
        if (chartsWithNewerVersions.size === 0 || hasFetchedRef.current) {
            return;
        }
        hasFetchedRef.current = true;
        const fetchVersionInfo = async () => {
            setIsLoading(true);
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const versionRows = [];
            const affectedRefs = chartRefs.filter((ref) => chartsWithNewerVersions.has(ref.libraryResourceId || ref.id));
            for (const ref of affectedRefs) {
                try {
                    const chartId = ref.libraryResourceId || ref.id;
                    const chartResource = await libraryManager.getResourceById(chartId, ResourceType.REPORTING_CHART);
                    if (!chartResource) {
                        continue;
                    }
                    const chartKey = chartResource.key;
                    if (!chartKey) {
                        continue;
                    }
                    const response = await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                        key: chartKey,
                        status: ResourceStatus.PUBLISHED,
                    });
                    const publishedVersions = response?.data || [];
                    const currentVersionNum = parseInt(chartResource.version || '1', 10);
                    const latestActive = publishedVersions
                        .filter((v) => v.active && v.id !== chartResource.id)
                        .sort((a, b) => parseInt(b.version || '0', 10) - parseInt(a.version || '0', 10))[0];
                    if (latestActive) {
                        versionRows.push({
                            chartRefId: ref.id,
                            chartName: chartResource.name || ref.name,
                            description: chartResource.metadata?.description || '',
                            currentVersion: `v${currentVersionNum}`,
                            latestVersion: `v${latestActive.version}`,
                            latestResourceId: latestActive.id,
                            latestName: latestActive.name,
                            latestTitle: latestActive.data?.title || ref.title,
                            latestType: latestActive.data?.type || ref.type,
                            action: '',
                        });
                    }
                }
                catch {
                    // Skip charts that fail to load
                }
            }
            setRows(versionRows);
            setIsLoading(false);
        };
        fetchVersionInfo();
    }, [isOpen, chartsWithNewerVersions, chartRefs]);
    const handleActionChange = useCallback((chartRefId, option) => {
        if (!option) {
            return;
        }
        setRows((prev) => prev.map((r) => (r.chartRefId === chartRefId ? { ...r, action: option.value } : r)));
    }, []);
    // Apply is enabled as soon as at least one row has any action chosen —
    // blank rows are treated as implicit "Keep current" so the user doesn't
    // have to mark every row before updating just one. Mirrors the chart-builder
    // Review versions modal (SSP-3464).
    const hasAnyAction = useMemo(() => rows.some((r) => r.action !== ''), [rows]);
    const hasUpdates = useMemo(() => rows.some((r) => r.action === 'UPDATE'), [rows]);
    const handleApply = useCallback(async () => {
        if (!hasUpdates) {
            onClose();
            return;
        }
        const updatedRefs = chartRefs.map((ref) => {
            const row = rows.find((r) => r.chartRefId === ref.id);
            if (!row || row.action !== 'UPDATE') {
                return ref;
            }
            return {
                ...ref,
                libraryResourceId: row.latestResourceId,
                name: row.latestName,
                title: row.latestTitle,
                type: row.latestType,
            };
        });
        setIsSaving(true);
        try {
            await onApply(updatedRefs);
            onClose();
        }
        finally {
            setIsSaving(false);
        }
    }, [chartRefs, rows, hasUpdates, onApply, onClose]);
    const actionOptions = useMemo(() => ACTION_OPTIONS.map((o) => ({ ...o, label: translate(o.label) })), [translate]);
    const columns = useMemo(() => [
        {
            header: translate('Name'),
            accessorKey: 'chartName',
            cell: ({ row }) => (_jsx(NucleusLink, { href: `/library/charts/${row.original.latestResourceId}`, target: '_blank', children: _jsxs("span", { className: 'flex items-center gap-1', children: [row.original.chartName, _jsx(Icon, { name: 'externalLink', size: 'sm', color: 'current' })] }) })),
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            cell: ({ row }) => (_jsx(Text, { size: 'sm', className: 'truncate', children: row.original.description || '-' })),
        },
        {
            header: translate('Study version'),
            accessorKey: 'currentVersion',
            cell: ({ row }) => {
                // Preview the bumped chart-ref version (currentVersion → latestVersion)
                // only when (a) the user picked Update on this row AND (b) the
                // dashboard is PUBLISHED, since only PUBLISHED dashboards
                // auto-create a new version on Apply (via cloneDashboard +
                // archiveDashboard). DRAFT dashboards update chartRefs in place
                // without bumping the dashboard's own version, so showing the
                // arrow there would imply a state change that isn't happening —
                // matches the chart-builder modal convention (SSP-3464).
                const showArrow = row.original.action === 'UPDATE' && dashboardStatus === 'PUBLISHED';
                if (!showArrow) {
                    return _jsx(Badge, { variant: 'common', label: row.original.currentVersion });
                }
                return (_jsxs("div", { className: 'flex items-center gap-1', children: [_jsx(Badge, { variant: 'common', label: row.original.currentVersion }), _jsx("span", { className: 'text-text-secondary', children: "\u2192" }), _jsx(Badge, { variant: 'common', label: row.original.latestVersion })] }));
            },
            size: 180,
        },
        {
            header: translate('Library version'),
            accessorKey: 'latestVersion',
            cell: ({ row }) => _jsx(Badge, { variant: 'common', label: row.original.latestVersion }),
            size: 140,
        },
        {
            header: '',
            accessorKey: 'action',
            cell: ({ row }) => {
                const selectedAction = actionOptions.find((o) => o.value === row.original.action);
                return (_jsx(Select, { placeholder: translate('--Select--'), options: actionOptions, value: selectedAction, onChange: (option) => handleActionChange(row.original.chartRefId, option), width: 'full' }));
            },
            size: 180,
        },
    ], [translate, actionOptions, handleActionChange, dashboardStatus]);
    const footerPrimaryButton = {
        label: translate('Apply'),
        onClick: handleApply,
        disabled: !hasAnyAction || isLoading || isSaving,
        ...(isSaving && { content: _jsx(Spinner, { size: 'sm' }) }),
    };
    const footerSecondaryButtons = [
        {
            label: translate('Cancel'),
            onClick: onClose,
            disabled: isSaving,
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Review versions'), isOpen: isOpen, closeWindow: onClose, footerPrimaryActionButton: footerPrimaryButton, footerSecondaryActionButtons: footerSecondaryButtons, width: 'full', children: isLoading ? (_jsx("div", { className: 'flex items-center justify-center py-16', children: _jsx(Spinner, {}) })) : rows.length === 0 ? (_jsx("div", { className: 'flex items-center justify-center py-16', children: _jsx(Text, { className: 'text-text-secondary', children: translate('All charts are up to date.') }) })) : (_jsxs("div", { className: 'flex flex-col gap-4', children: [dashboardStatus === 'PUBLISHED' && (_jsx(InlineNotification, { type: 'info', title: translate('A new version of this dashboard will be created'), subtitle: translate('This dashboard is in Ready status and cannot be modified directly. Applying updates will create a new version (In progress) and archive the current one.') })), _jsx(Table, { columns: columns, data: rows, getRowId: (row) => row.chartRefId, labelNoResults: translate('No charts to review.') })] })) }));
};
